// deps
import React, { useMemo, useCallback, memo, RefObject, useEffect } from "react";
import classNames from "classnames";

// internal deps
import { ScreenReaderOnly, AriaHidden, format, priceInRupee, RightArrow, isEmpty } from "@swiggy-private/core-ui";

// interfaces
import { PlanDetailsItem, RenderingDetails } from "@swiggy-private/cart-types";
import { AddSuperProps } from "@super-ui/interfaces";

// constants
import { TEXTS, ACCESSIBLITY_TEXTS, SELECTED_PLAN_MODE } from "@super-ui/constants/SwiggyOne";
import { ONE_WIDGET_TEST_IDS } from "@super-ui/test-ids/one-widget";

// helpers
import { getPrefixedTestId } from "@super-ui/helpers/String";

interface PlanCta {
    type?: string;
    text?: string;
    link?: string;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    visible_message?: string;
}
import styles from "../styles/SelectedPlan.v4.scss";

export interface SelectedPlanProps {
    itemData: RenderingDetails;
    planDetails: PlanDetailsItem;
    cta: PlanCta;
    addSuper: (addSuperProps: AddSuperProps) => void;
    removeSuper: (planId: string, planName: string, planPrice: string) => void;
    changeSuper: (plan: PlanDetailsItem, ctaLink: string, isOneTap: boolean) => void;
    impressionSuperChangePlan?: (
        planName: string,
        ctaLink: string,
        planPrice: string,
        itemData: RenderingDetails,
    ) => void;
    impressionSuperOneTapAddNudge?: (payload: {
        title: string;
        ctaText: string;
        planId: string;
        planName: string;
        planPrice: string;
        subtitle?: string;
    }) => void;
    changeSuperBtnRef: RefObject<HTMLButtonElement> | RefObject<HTMLDivElement>;
    testIdPrefix?: string;
    mode?: string;
    showViewDetailsFooter: boolean;
}

const SelectedPlan: React.FC<SelectedPlanProps> = ({
    itemData,
    planDetails,
    cta,
    addSuper,
    removeSuper,
    changeSuper,
    impressionSuperChangePlan,
    impressionSuperOneTapAddNudge,
    changeSuperBtnRef,
    testIdPrefix,
    mode,
    showViewDetailsFooter,
}: SelectedPlanProps) => {
    const { name, base_price: basePrice, final_price: finalPrice, plan_id: planId } = planDetails;

    useEffect(() => {
        if (typeof impressionSuperChangePlan === "function") {
            impressionSuperChangePlan(name, cta?.link || "--", priceInRupee(finalPrice), itemData);
        }
        if (typeof impressionSuperOneTapAddNudge === "function" && mode === SELECTED_PLAN_MODE.ADD) {
            impressionSuperOneTapAddNudge({
                title: itemData.display_text,
                ctaText: TEXTS.ADD,
                planId,
                planName: name,
                planPrice: priceInRupee(finalPrice),
            });
        }
    }, [name, finalPrice, itemData, impressionSuperChangePlan, impressionSuperOneTapAddNudge, mode, planId, cta?.link]);

    const getScreenReaderText = useMemo(() => {
        if (basePrice !== finalPrice) {
            return format(ACCESSIBLITY_TEXTS.COST_WITH_DISCOUNT, priceInRupee(basePrice), priceInRupee(finalPrice));
        }
        return format(ACCESSIBLITY_TEXTS.COST, priceInRupee(finalPrice));
    }, [basePrice, finalPrice]);

    const [selectionContainerTestId, changeBtnTestId, removeBtnTestId, addBtnTestId]: string[] = useMemo(
        () => [
            getPrefixedTestId(ONE_WIDGET_TEST_IDS.SELECT_CONTAINER, testIdPrefix),
            getPrefixedTestId(ONE_WIDGET_TEST_IDS.CHANGE_PLAN, testIdPrefix),
            getPrefixedTestId(ONE_WIDGET_TEST_IDS.REMOVE_PLAN, testIdPrefix),
            getPrefixedTestId(ONE_WIDGET_TEST_IDS.ADD_PLAN, testIdPrefix),
        ],
        [testIdPrefix],
    );

    const onRemoveClick = useCallback(() => {
        if (typeof removeSuper === "function") {
            removeSuper(planId, name, priceInRupee(finalPrice));
        }
    }, [removeSuper, planId, name, finalPrice]);

    const onAddClick = useCallback(() => {
        if (typeof addSuper === "function") {
            const addSuperProps: AddSuperProps = {
                title: itemData.display_text,
                subtitle: itemData.meta?.subtitles?.[0] || "",
                cta: TEXTS.ADD,
                plan: planDetails,
            };
            addSuper(addSuperProps);
        }
    }, [addSuper, itemData.display_text, itemData.meta, planDetails]);

    const onChangeClick = useCallback(() => {
        if (typeof changeSuper === "function") {
            changeSuper(planDetails, cta?.link || "", mode === SELECTED_PLAN_MODE.ADD);
        }
    }, [changeSuper, cta, mode, planDetails]);

    const mainPlanContainerClass = classNames({
        [styles.planContainer]: true,
        [styles.planContainerWithFooter]: showViewDetailsFooter,
    });

    return (
        <div className={mainPlanContainerClass} data-testid={selectionContainerTestId}>
            <div className={styles.planDetail}>
                <ScreenReaderOnly text={format(ACCESSIBLITY_TEXTS.ONE_SUBSCRIPTION, name)} />
                <AriaHidden as="div" className={styles.planDetailName}>
                    {name}
                </AriaHidden>
                {/* if loyalty is not sending any cta?.text stop showing this in UI */}
                {!isEmpty(cta?.text) && (
                    <button
                        className={styles.planChange}
                        aria-label={cta?.text || TEXTS.CHANGE_PLAN}
                        onClick={onChangeClick}
                        ref={changeSuperBtnRef as RefObject<HTMLButtonElement>}
                        data-testid={changeBtnTestId}
                    >
                        {cta?.text || TEXTS.CHANGE_PLAN} <RightArrow className={styles.planChangeArrow} />
                    </button>
                )}
            </div>
            {mode === SELECTED_PLAN_MODE.REMOVE && (
                <button className={styles.planRemove} onClick={onRemoveClick} data-testid={removeBtnTestId}>
                    {TEXTS.REMOVE}
                </button>
            )}
            {mode === SELECTED_PLAN_MODE.ADD && (
                <button className={styles.planAdd} onClick={onAddClick} data-testid={addBtnTestId}>
                    {TEXTS.ADD}
                </button>
            )}
            <div className={styles.planPrice}>
                <ScreenReaderOnly text={getScreenReaderText} />
                {basePrice !== finalPrice && (
                    <AriaHidden as="span" className={styles.planPriceBase}>
                        {priceInRupee(basePrice)}
                    </AriaHidden>
                )}
                <AriaHidden as="span" className={styles.planPriceFinal}>
                    {priceInRupee(finalPrice)}
                </AriaHidden>
            </div>
        </div>
    );
};

export default memo(SelectedPlan);
